import { SIGN_OUT_TIMEOUT_MS } from '@/lib/performSignOut';

/**
 * GoTrue `POST /auth/v1/logout` can hang after idle timeout (expired refresh
 * token / dead socket). The supabase-js client holds its auth lock until that
 * request settles, so a later `signInWithPassword` never runs. Aborting this
 * one path lets sign-out finish and releases the lock.
 */
export function isGoTrueLogoutUrl(url: string): boolean {
  try {
    const parsed = new URL(url, 'http://local.invalid');
    return parsed.pathname.includes('/auth/') && parsed.pathname.endsWith('/logout');
  } catch {
    return /\/auth\/v1\/logout/i.test(url);
  }
}

export function fetchWithLogoutTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs: number = SIGN_OUT_TIMEOUT_MS,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (!isGoTrueLogoutUrl(url)) {
    return fetchImpl(input, init);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const upstream = init?.signal;
  if (upstream) {
    if (upstream.aborted) {
      controller.abort();
    } else {
      upstream.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  return fetchImpl(input, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
  });
}
